import React from 'react';
import { SpecChange } from './siteSpec';

/**
 * What the agent just changed, listed under the transcript.
 *
 * WHY IT EXISTS. Chat and fields now share one column, so the fields are behind
 * a tab while you talk — and watching values move as the agent worked was half
 * of what made that panel worth having. This puts the same information where
 * you already are, and improves on it: "Header · #0c1e28, Banner · #0c1e28,
 * Links · 4 links" is a named list, where the rail only ever gave you a colour
 * shifting in the corner of your eye.
 *
 * Every chip is a way IN. Clicking one opens the design tab with that field
 * highlighted, which makes the strip the bridge between the two halves rather
 * than a second read-only view of the same thing.
 */
export default function SpecChangeStrip({
  changes,
  onOpenField,
  onDismiss,
}: {
  changes: SpecChange[];
  onOpenField: (path: string) => void;
  onDismiss: () => void;
}) {
  if (!changes.length) return null;
  return (
    <div className="px-3 py-2 border-t border-gray-200 dark:border-gray-700 bg-blue-50/60 dark:bg-blue-900/10">
      <div className="flex items-baseline justify-between gap-2 mb-1">
        <span className="text-[11px] font-medium text-blue-800 dark:text-blue-300">
          Changed {changes.length === 1 ? 'this' : `these ${changes.length}`}
        </span>
        <button
          type="button"
          onClick={onDismiss}
          className="text-[11px] text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
        >
          Dismiss
        </button>
      </div>
      <div className="flex flex-wrap gap-1">
        {changes.map((change) => (
          <button
            key={change.path}
            type="button"
            onClick={() => onOpenField(change.path)}
            title={`Open ${change.path} in the design tab`}
            className="px-1.5 py-0.5 rounded text-[11px] bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-800 text-gray-700 dark:text-gray-200 hover:border-blue-500"
          >
            {change.label} <span className="text-gray-400 dark:text-gray-500">·</span>{' '}
            <span className="font-mono">{change.value}</span>
          </button>
        ))}
      </div>
    </div>
  );
}
